import type { EpcProjectMembershipRow, EpcProjectOrgUnit } from '../../../../services/epcProjectService';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import {
  applyOrgUnitToTaskIndices,
  assignableMembersForOrg,
  orgUnitKey,
  taskOrgUnitId,
} from './epcScheduleDepartmentAssignment';

/** Words we ignore while keyword-matching task names against department focus areas. */
const STOP_WORDS = new Set<string>([
  'a', 'an', 'and', 'as', 'at', 'be', 'by', 'for', 'from', 'in', 'is', 'it', 'of',
  'on', 'or', 'the', 'to', 'with', 'into', 'onto', 'per', 'vs',
  'work', 'works', 'activity', 'activities', 'task', 'tasks', 'phase', 'stage',
  'milestone', 'package', 'start', 'finish', 'complete', 'completion', 'review',
  'final', 'draft',
]);

/** Synonyms/domain-specific aliases per canonical keyword. Helps a single focus tag match more task names. */
const ALIASES: Record<string, string[]> = {
  civil: ['civil', 'concrete', 'foundation', 'footing', 'excavation', 'earthworks', 'rcc', 'piling', 'formwork', 'rebar', 'structural', 'structure', 'masonry', 'plinth', 'slab', 'beam'],
  electrical: ['electrical', 'cable', 'cabling', 'panel', 'switchgear', 'transformer', 'busbar', 'wiring', 'earthing', 'grounding', 'substation', 'ht', 'lt', 'mv', 'lv'],
  mechanical: ['mechanical', 'piping', 'pipe', 'welding', 'pump', 'compressor', 'hvac', 'duct', 'valve', 'tank', 'vessel', 'boiler', 'turbine'],
  instrumentation: ['instrumentation', 'instrument', 'plc', 'dcs', 'scada', 'sensor', 'transmitter', 'calibration'],
  design: ['design', 'engineering', 'drawing', 'layout', 'gad', 'basic', 'detailed', 'bom', 'cad', 'bim', 'modeling'],
  procurement: ['procurement', 'purchase', 'purchasing', 'po', 'order', 'rfq', 'vendor', 'supply', 'delivery', 'logistics', 'shipment'],
  qa: ['qa', 'qc', 'quality', 'inspection', 'inspect', 'ndt', 'testing', 'witness', 'punchlist', 'punch'],
  safety: ['safety', 'hse', 'ehs', 'hazop', 'hira', 'risk', 'permit', 'ppe', 'lota'],
  commissioning: ['commissioning', 'pre-commissioning', 'precommissioning', 'startup', 'start-up', 'handover', 'handoff', 'energisation', 'energization', 'loop', 'performance'],
  erection: ['erection', 'installation', 'install', 'mounting', 'lifting', 'rigging', 'assembly', 'alignment'],
  survey: ['survey', 'layout', 'marking', 'levelling', 'leveling', 'topo', 'gps'],
  planning: ['planning', 'schedule', 'monitoring', 'progress', 'report', 'mis', 'tracking'],
};

function tokenize(text: string): string[] {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length >= 2 && !STOP_WORDS.has(w));
}

function expandFocusTokens(focus: string): string[] {
  const base = tokenize(focus);
  const out = new Set<string>(base);
  for (const tok of base) {
    const alias = ALIASES[tok];
    if (alias) for (const a of alias) out.add(a);
  }
  return Array.from(out);
}

function focusIndexForOrgUnits(orgUnits: EpcProjectOrgUnit[]): Map<string, Set<string>> {
  const idx = new Map<string, Set<string>>();
  for (const u of orgUnits) {
    if (u.inactive) continue;
    const id = orgUnitKey(u);
    const set = new Set<string>();
    const hay: string[] = [];
    hay.push(u.name || '');
    const focusAreas = (u.focusAreas ?? []) as string[];
    for (const fa of focusAreas) hay.push(fa);
    for (const raw of hay) {
      for (const tok of expandFocusTokens(raw)) set.add(tok);
    }
    if (set.size > 0) idx.set(id, set);
  }
  return idx;
}

export type AutoAssignSuggestion = {
  orgId: string;
  score: number;
  /** Tokens from the task name that matched the department's focus areas. */
  matchedTokens: string[];
};

/**
 * Score each department against a task name. Higher is better; returns sorted suggestions.
 * Score = unique matched tokens + 0.5 bonus when department name itself appears in the task name.
 */
export function suggestOrgUnitsForTask(
  task: EpcScheduleTaskRow,
  orgUnits: EpcProjectOrgUnit[],
  focusIndex?: Map<string, Set<string>>
): AutoAssignSuggestion[] {
  const idx = focusIndex ?? focusIndexForOrgUnits(orgUnits);
  const nameTokens = new Set(tokenize(String(task.name ?? '')));
  if (nameTokens.size === 0) return [];
  const suggestions: AutoAssignSuggestion[] = [];
  for (const u of orgUnits) {
    if (u.inactive) continue;
    const id = orgUnitKey(u);
    const focus = idx.get(id);
    if (!focus || focus.size === 0) continue;
    const matched: string[] = [];
    for (const tok of nameTokens) if (focus.has(tok)) matched.push(tok);
    if (matched.length === 0) continue;
    const deptNameTokens = new Set(tokenize(u.name || ''));
    let bonus = 0;
    for (const tok of deptNameTokens) if (nameTokens.has(tok)) bonus += 0.5;
    suggestions.push({ orgId: id, score: matched.length + bonus, matchedTokens: matched });
  }
  suggestions.sort((a, b) => b.score - a.score);
  return suggestions;
}

export type AutoAssignResult = {
  tasks: EpcScheduleTaskRow[];
  /** Row indices that received a new department id. */
  changed: number[];
  /** Row indices that had no confident suggestion. */
  skipped: number[];
  /** How many rows were already assigned and not touched. */
  preserved: number;
};

/**
 * Auto-assign departments for every unassigned task using keyword matches.
 * If `overwrite` is true, re-assigns already-assigned rows too.
 * `minScore` gates the suggestion confidence (default 1 matched token).
 */
export function autoAssignDepartments(
  tasks: EpcScheduleTaskRow[],
  orgUnits: EpcProjectOrgUnit[],
  memberships: EpcProjectMembershipRow[],
  opts: { overwrite?: boolean; minScore?: number } = {}
): AutoAssignResult {
  const overwrite = opts.overwrite === true;
  const minScore = opts.minScore ?? 1;
  const idx = focusIndexForOrgUnits(orgUnits);

  const byOrg = new Map<string, number[]>();
  const skipped: number[] = [];
  let preserved = 0;

  for (let i = 0; i < tasks.length; i += 1) {
    const row = tasks[i]!;
    const cur = taskOrgUnitId(row);
    if (cur && !overwrite) {
      preserved += 1;
      continue;
    }
    const sugg = suggestOrgUnitsForTask(row, orgUnits, idx);
    const top = sugg[0];
    if (!top || top.score < minScore) {
      skipped.push(i);
      continue;
    }
    const list = byOrg.get(top.orgId) ?? [];
    list.push(i);
    byOrg.set(top.orgId, list);
  }

  let next = tasks;
  const changed: number[] = [];
  for (const [orgId, indices] of byOrg) {
    next = applyOrgUnitToTaskIndices(next, indices, orgId, memberships);
    for (const j of indices) changed.push(j);
  }

  return { tasks: next, changed, skipped, preserved };
}

/** Round-robin assignable user for a given org (by member role priority). Returns undefined if none. */
export function pickAssigneeForOrg(
  memberships: EpcProjectMembershipRow[],
  orgId: string | undefined,
  seedIndex: number
): string | undefined {
  const pool = assignableMembersForOrg(memberships, orgId);
  if (pool.length === 0) return undefined;
  const managers = pool.filter((m) => m.role === 'manager');
  const engineers = pool.filter((m) => m.role === 'site_engineer');
  const ordered = [...engineers, ...managers];
  if (ordered.length === 0) return undefined;
  return ordered[seedIndex % ordered.length]?.userId;
}

/**
 * Auto-balance the assigned user across site engineers / managers of each org.
 * Useful after department auto-assignment to distribute workload.
 */
export function autoBalanceAssignees(
  tasks: EpcScheduleTaskRow[],
  memberships: EpcProjectMembershipRow[],
  opts: { overwrite?: boolean } = {}
): EpcScheduleTaskRow[] {
  const overwrite = opts.overwrite === true;
  const seedByOrg = new Map<string, number>();
  return tasks.map((t) => {
    const oid = taskOrgUnitId(t);
    if (!oid) return t;
    const cur = t.assignedUserId;
    if (cur && !overwrite) return t;
    const seed = seedByOrg.get(oid) ?? 0;
    const uid = pickAssigneeForOrg(memberships, oid, seed);
    seedByOrg.set(oid, seed + 1);
    if (!uid) return t;
    return { ...t, assignedUserId: uid };
  });
}
